// Ported from primus-admin-main/src/utils/wsAdmin.js, adapted for this app's
// auth storage (zustand-persisted 'primus-auth-storage', not 'primus_jwt')
// and API base (VITE_API_URL, not getApiBase()). No token-refresh-before-
// connect step here -- unlike the café-admin app, this app has no client-side
// refresh-token rotation; a rejected (1008) token just retries with backoff.

class AdminWebSocketManager {
    constructor() {
        this.socket = null;
        this.subscribers = new Set();
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 10;
        this.reconnectInterval = 3000;
        this.isConnecting = false;
        this.pingInterval = null;
    }

    getToken() {
        try {
            const stored = localStorage.getItem('primus-auth-storage');
            if (!stored) return null;
            return JSON.parse(stored)?.state?.token || null;
        } catch {
            return null;
        }
    }

    getWsUrl() {
        const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
        // Strip a trailing /api (or /api/) — /ws/admin is mounted at the root,
        // not under /api.
        const root = apiBase.replace(/\/api\/?$/, '');
        return `${root.replace(/^http/, 'ws')}/ws/admin`;
    }

    connect() {
        if (this.socket || this.isConnecting) return;
        this.isConnecting = true;

        const token = this.getToken();
        if (!token) {
            console.warn('[WS] No auth token found, cannot connect to admin WebSocket');
            this.isConnecting = false;
            return;
        }
        const url = this.getWsUrl();

        try {
            this.socket = new WebSocket(url);

            this.socket.onopen = () => {
                this.isConnecting = false;
                this.reconnectAttempts = 0;
                this.socket.send(JSON.stringify({
                    event: 'auth',
                    payload: { token },
                    ts: Math.floor(Date.now() / 1000),
                }));
                this.startPing();
            };

            this.socket.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    if (data.event === 'auth.error') {
                        console.error('[WS] Auth Error:', data.payload?.reason || 'unauthorized');
                        this.socket.close();
                        return;
                    }
                    this.subscribers.forEach((callback) => {
                        try { callback(data); } catch (err) { console.error('[WS] Subscriber callback error:', err); }
                    });
                } catch {
                    // Ignore non-JSON messages
                }
            };

            this.socket.onclose = (event) => {
                this.socket = null;
                this.isConnecting = false;
                this.stopPing();

                if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
                    this.reconnectAttempts++;
                    const delay = this.reconnectInterval * Math.min(this.reconnectAttempts, 5);
                    setTimeout(() => this.connect(), delay);
                }
            };

            this.socket.onerror = () => {
                // socket.onclose handles reconnection
            };
        } catch (err) {
            this.isConnecting = false;
            console.error('[WS] Fatal connection error:', err);
        }
    }

    startPing() {
        this.stopPing();
        this.pingInterval = setInterval(() => {
            if (this.socket?.readyState === WebSocket.OPEN) {
                this.socket.send(JSON.stringify({ event: 'heartbeat', ts: Math.floor(Date.now() / 1000) }));
            }
        }, 30000);
    }

    stopPing() {
        if (this.pingInterval) {
            clearInterval(this.pingInterval);
            this.pingInterval = null;
        }
    }

    send(obj) {
        if (this.socket?.readyState === WebSocket.OPEN) {
            try {
                this.socket.send(JSON.stringify(obj));
                return true;
            } catch {
                return false;
            }
        }
        return false;
    }

    subscribe(callback) {
        this.subscribers.add(callback);
        if (!this.socket && !this.isConnecting) this.connect();
        return () => { this.subscribers.delete(callback); };
    }

    disconnect() {
        this.stopPing();
        if (this.socket) {
            this.socket.close(1000, 'Normal closure');
            this.socket = null;
        }
        this.subscribers.clear();
        this.isConnecting = false;
    }
}

const adminWs = new AdminWebSocketManager();

export const subscribe = (callback) => adminWs.subscribe(callback);
export const send = (obj) => adminWs.send(obj);

export default adminWs;
